import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Video, Clock, Calendar, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusLabel(status) {
  switch (status) {
    case 'starting':
    case 'active':
      return { text: 'Recording…', cls: 'bg-red-500/20 text-red-300 border-red-500/40' };
    case 'ending':
      return { text: 'Finalizing…', cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' };
    case 'completed':
      return { text: 'Ready', cls: 'bg-green-500/20 text-green-300 border-green-500/40' };
    case 'failed':
    case 'aborted':
      return { text: 'Failed', cls: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40' };
    default:
      return { text: status || 'Unknown', cls: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40' };
  }
}

export default function SessionRecordingsPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlayback, setActivePlayback] = useState(null); // {url, recording_id}
  const [loadingPlay, setLoadingPlay] = useState(null);

  const refresh = async () => {
    try {
      const res = await api().get(`/livekit/recordings/${sessionId}`);
      setRecordings(res.data.recordings || []);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to load recordings';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Smart polling: only poll while at least one recording is in a non-terminal state
  useEffect(() => {
    const TERMINAL = new Set(['completed', 'failed', 'aborted', 'limit']);
    const stillRunning = recordings.some(r => !TERMINAL.has(r.status));
    if (!stillRunning) return undefined;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordings]);

  const handlePlay = async (rec) => {
    if (rec.status !== 'completed') {
      toast.info('Recording is still finalizing — try again in a moment');
      return;
    }
    setLoadingPlay(rec.id);
    try {
      const res = await api().get(`/livekit/recording/${rec.id}/playback-url`);
      setActivePlayback({ url: res.data.playback_url, recording_id: rec.id });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to load replay';
      toast.error(detail);
    } finally {
      setLoadingPlay(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="session-recordings-page">
      <header className="border-b border-border px-6 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground"
          aria-label="back"
          data-testid="recordings-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-heading text-2xl uppercase tracking-wider">Session Replays</h1>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="recordings-loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : recordings.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border" data-testid="recordings-empty">
            <Video className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No recordings for this session yet.</p>
            <p className="text-xs text-muted-foreground mt-2">
              The host can start a recording from inside the live room.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="recordings-list">
            {recordings.map((rec) => {
              const lbl = statusLabel(rec.status);
              return (
                <div
                  key={rec.id}
                  className="bg-card border border-border p-4 flex items-center justify-between gap-4"
                  data-testid={`recording-row-${rec.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${lbl.cls}`}>
                        {lbl.text}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {formatDate(rec.started_at)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formatDuration(rec.duration_seconds)}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handlePlay(rec)}
                    disabled={rec.status !== 'completed' || loadingPlay === rec.id}
                    data-testid={`recording-play-${rec.id}`}
                  >
                    {loadingPlay === rec.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <><Play className="h-4 w-4 mr-1" /> Play</>
                    }
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {activePlayback && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setActivePlayback(null)}
          data-testid="recording-player-modal"
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <video
              src={activePlayback.url}
              controls
              autoPlay
              className="w-full rounded shadow-2xl"
              data-testid="recording-video"
            />
            <div className="text-center mt-3">
              <Button variant="outline" onClick={() => setActivePlayback(null)} data-testid="recording-close-btn">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
